export type BranchImage = {
  branchNumber: string
  thumbnail: string
  thumbnailMobile: string
}

type WithBranchNumber = { branchNumber: string | number }

/**
 * Branch numbers are hand-entered in Umbraco and typed as int32 in the REST API,
 * so "068", "68" and "68 " all refer to the same branch. Comparing the raw values
 * would hide a live branch over a typo, so both sides are normalised first.
 */
export function normalizeBranchNumber(value?: string | number | null) {
  const raw = value?.toString().trim() ?? ""
  if (!raw) {
    return ""
  }
  const numeric = Number(raw)
  return Number.isFinite(numeric) ? numeric.toString() : raw.toLowerCase()
}

/**
 * Keeps only the branches that are published in the CMS (END-289).
 *
 * The branch lists come from the REST API, which keeps returning a branch after it
 * is unpublished in Umbraco. Umbraco returns published nodes only, so a matching
 * node is what proves a branch should be visible.
 *
 * Fails open when the CMS returned no branch nodes at all, which happens when the
 * Umbraco query errors (`branches/page.tsx` swallows that into an empty edge list)
 * and on cultures with no published branch nodes, such as en-us today. Filtering on
 * an empty list would blank the branch locator entirely, so every branch is kept
 * instead and only the thumbnails are missing.
 */
export function filterPublishedBranches<T extends WithBranchNumber>(
  branches: T[],
  branchImages: BranchImage[],
): T[] {
  const publishedNumbers = new Set(
    branchImages
      .map((image) => normalizeBranchNumber(image.branchNumber))
      .filter(Boolean),
  )

  if (publishedNumbers.size === 0) {
    return branches
  }

  return branches.filter((branch) =>
    publishedNumbers.has(normalizeBranchNumber(branch.branchNumber)),
  )
}

/**
 * Looks up a branch's Umbraco media using the same normalisation as
 * `filterPublishedBranches`, so a branch that passes the filter cannot then fail its
 * thumbnail lookup and fall back to an empty image source.
 */
export function findBranchImage(
  branchImages: BranchImage[],
  branch: WithBranchNumber,
) {
  const target = normalizeBranchNumber(branch.branchNumber)
  if (!target) {
    return undefined
  }
  return branchImages.find(
    (image) => normalizeBranchNumber(image.branchNumber) === target,
  )
}
